import React, {useState} from 'react';
import {Search, X} from 'lucide-react';
import Card from '../../../../Presentation/components/ui/Card';
import AppInput from '../../../../Presentation/components/ui/AppInput';
import SearchableDropdown from '../../../../Presentation/components/ui/SearchableDropdown';
import {useVisitFilterOptions} from '../../hooks/useVisitFilterOptions';
import {VisitFilters} from '../../models/visit.model';

/** The subset of VisitFilters this toolbar controls (status lives in the tabs). */
export type VisitListFilterValues = Pick<
  VisitFilters,
  'brand_id' | 'visitor_id' | 'store_id' | 'date_from' | 'date_to'
>;

interface VisitsListFiltersProps {
  onApply: (filters: VisitListFilterValues) => void;
  onClear: () => void;
}

const fieldLabelClass = 'block mb-1 text-sm font-medium text-gray-700';

/**
 * Filter toolbar for the visits list: brand, marketer-developer, store, and a
 * date period. Selections are staged locally and committed on "تصفية" (Apply)
 * — mirroring StoresListFilters — so we issue one request per apply rather than
 * one per keystroke/selection. The active status tab is preserved across applies
 * (handled by the management hook merging into existing filters).
 */
const VisitsListFilters: React.FC<VisitsListFiltersProps> = ({onApply, onClear}) => {
  const {
    brandOptions, brandsLoading,
    developerOptions, developersLoading,
    storeOptions, storesLoading, searchStores
  } = useVisitFilterOptions();

  const [brandId, setBrandId] = useState('');
  const [developerId, setDeveloperId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const handleApply = () => {
    onApply({
      brand_id: brandId ? Number(brandId) : undefined,
      visitor_id: developerId ? Number(developerId) : undefined,
      store_id: storeId ? Number(storeId) : undefined,
      date_from: dateFrom || undefined,
      // Backend filters with an inclusive `<=` on a timestamp column, so extend
      // the "to" date to end-of-day; otherwise same-day visits are excluded.
      date_to: dateTo ? `${dateTo}T23:59:59` : undefined
    });
  };

  const handleClear = () => {
    setBrandId('');
    setDeveloperId('');
    setStoreId('');
    setDateFrom('');
    setDateTo('');
    onClear();
  };

  return (
    <Card className="p-6">
      <div className="flex gap-4 flex-wrap items-end">
        {/* Brand */}
        <div className="min-w-[200px] flex-1">
          <label className={fieldLabelClass}>البراند</label>
          <SearchableDropdown
            value={brandId}
            options={brandOptions}
            isLoading={brandsLoading}
            onChange={setBrandId}
            placeholder="كل البراندات"
            searchPlaceholder="ابحث عن براند..."
            emptyMessage="لا توجد براندات"
            loadingMessage="جاري التحميل..."
          />
        </div>

        {/* Marketer-developer */}
        <div className="min-w-[220px] flex-1">
          <label className={fieldLabelClass}>المسوّق</label>
          <SearchableDropdown
            value={developerId}
            options={developerOptions}
            isLoading={developersLoading}
            onChange={setDeveloperId}
            placeholder="كل المسوّقين"
            searchPlaceholder="ابحث بالاسم أو اسم المستخدم..."
            emptyMessage="لا يوجد مسوّقون"
            loadingMessage="جاري التحميل..."
          />
        </div>

        {/* Store (server-side search) */}
        <div className="min-w-[220px] flex-1">
          <label className={fieldLabelClass}>المتجر</label>
          <SearchableDropdown
            value={storeId}
            options={storeOptions}
            isLoading={storesLoading}
            onChange={setStoreId}
            onSearch={searchStores}
            placeholder="كل المتاجر"
            searchPlaceholder="اكتب اسم المتجر للبحث..."
            emptyMessage="اكتب للبحث عن متجر"
            loadingMessage="جاري البحث..."
          />
        </div>

        {/* Date period */}
        <div className="min-w-[160px]">
          <label className={fieldLabelClass}>من تاريخ</label>
          <AppInput
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={e => setDateFrom(e.target.value)}
          />
        </div>
        <div className="min-w-[160px]">
          <label className={fieldLabelClass}>إلى تاريخ</label>
          <AppInput
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            type="button"
            className="bg-brand-600 text-white px-6 py-2 rounded-md hover:bg-brand-700 transition-colors flex items-center gap-2"
            onClick={handleApply}
          >
            <Search className="w-4 h-4" />
            تصفية
          </button>
          <button
            type="button"
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2"
            onClick={handleClear}
          >
            <X className="w-4 h-4" />
            مسح
          </button>
        </div>
      </div>
    </Card>
  );
};

export default VisitsListFilters;
